import React, { useState, useEffect } from 'react';
import { User, Role } from '../../types/user';
import { EnhancedUser } from '../../types/enhancedUser';
import { useTheme } from '../../contexts/ThemeContext';
import { Language } from '../../services/organisationSettingsService';
import UserService from '../../services/userService';
import './UserForm.css';

interface UserFormProps {
  user: EnhancedUser | null;
  currentUserRole: Role;
  onSubmit: (user: Omit<User, 'id'> & { password?: string }) => void;
  onCancel: () => void;
}

const UserForm: React.FC<UserFormProps> = ({ user, currentUserRole, onSubmit, onCancel }) => {
  const { theme } = useTheme();
  const [name, setName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [landingPage, setLandingPage] = useState<string>('/dashboard');
  const [defaultLanguage, setDefaultLanguage] = useState<string>('');
  const [supportedLanguages, setSupportedLanguages] = useState<Language[]>([]);
  const [errors, setErrors] = useState<{
    name?: string;
    email?: string;
    password?: string;
    confirmPassword?: string;
  }>({});

  useEffect(() => {
    if (user) {
      setName(user.name);
      setEmail(user.email);
      setLandingPage(user.landing_page || '/dashboard');
      setDefaultLanguage(user.default_language || '');
      // Don't set password when editing - it should be changed separately
      setPassword('');
      setConfirmPassword('');
    } else {
      setName('');
      setEmail('');
      setLandingPage('/dashboard');
      setDefaultLanguage('');
      setPassword('');
      setConfirmPassword('');
    }
  }, [user]);

  // Load supported languages
  useEffect(() => {
    const loadLanguages = async () => {
      try {
        const languages = await UserService.getSupportedLanguages();
        setSupportedLanguages(languages);
      } catch (error) {
        console.error('Failed to load supported languages:', error);
        // Set default languages if API fails
        setSupportedLanguages([
          { code: 'en', name: 'English', nativeName: 'English' }
        ]);
      }
    };

    loadLanguages();
  }, []);

  const validate = () => {
    const newErrors: {
      name?: string;
      email?: string;
      password?: string;
      confirmPassword?: string;
    } = {};

    // Validate name
    if (!name.trim()) {
      newErrors.name = 'Name is required';
    }

    // Validate email
    if (!email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = 'Email is invalid';
    }

    // Only validate password for new users (not when editing)
    if (!user) {
      // Validate password
      if (!password) {
        newErrors.password = 'Password is required';
      } else if (password.length < 8) {
        newErrors.password = 'Password must be at least 8 characters';
      }

      // Validate confirm password
      if (password !== confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validate()) {
      return;
    }
    
    const userData: Omit<User, 'id'> & { password?: string } = {
      name: name.trim(),
      email: email.trim(),
      role: 'user', // Always set role to 'user' for organization admins
      landing_page: landingPage,
      default_language: defaultLanguage || undefined
    };

    // Only include password for new users
    if (!user && password) {
      userData.password = password;
    }
    
    onSubmit(userData);
  };

  return (
    <div className={`form-container theme-${theme}`}>
      <h2>{user ? 'Edit User' : 'Add New User'}</h2>
      
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="name">Name</label>
          <input
            type="text"
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Enter user name"
            autoFocus
            className={errors.name ? 'error' : ''}
          />
          {errors.name && <div className="error-text">{errors.name}</div>}
        </div>
        
        <div className="form-group">
          <label htmlFor="email">Email</label>
          <input
            type="email"
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter email address"
            className={errors.email ? 'error' : ''}
          />
          {errors.email && <div className="error-text">{errors.email}</div>}
        </div>
        
        <div className="form-group">
          <label htmlFor="landingPage">Landing Page</label>
          <select
            id="landingPage"
            value={landingPage}
            onChange={(e) => setLandingPage(e.target.value)}
          >
            <option value="/dashboard">Dashboard</option>
            <option value="/executive-search">Executive Search</option>
            <option value="/executive-chat">Executive Chat</option>
            <option value="/executive-xlsx-chat">Excel AI</option>
            <option value="/financial-data">Financial Data</option>
            <option value="/analytics">Analytics</option>
            <option value="/upload-csv">Upload CSV</option>
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="defaultLanguage">Default Language</label>
          <select
            id="defaultLanguage"
            value={defaultLanguage}
            onChange={(e) => setDefaultLanguage(e.target.value)}
          >
            <option value="">Use Organization Default</option>
            {supportedLanguages.map((language) => (
              <option key={language.code} value={language.code}>
                {language.name} ({language.nativeName})
              </option>
            ))}
          </select>
        </div>

        
        {/* Only show password fields for new users */}
        {!user && (
          <>
            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                className={errors.password ? 'error' : ''}
              />
              {errors.password && <div className="error-text">{errors.password}</div>}
            </div>
            
            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm Password</label>
              <input
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm password"
                className={errors.confirmPassword ? 'error' : ''}
              />
              {errors.confirmPassword && <div className="error-text">{errors.confirmPassword}</div>}
            </div>
          </>
        )}
        
        <div 
          style={{ 
            display: 'flex', 
            flexDirection: 'row', 
            gap: '12px', 
            justifyContent: 'flex-end', 
            alignItems: 'center', 
            marginTop: '24px',
            width: '100%',
            flexWrap: 'nowrap'
          }}
        >
          <button 
            type="submit" 
            className="primary-button"
            style={{ minWidth: '120px', flex: 'none', height: '44px', padding: '12px 16px' }}
          >
            {user ? 'Update' : 'Create'} User
          </button>
          <button 
            type="button" 
            className="secondary-button" 
            onClick={onCancel}
            style={{ minWidth: '100px', flex: 'none', height: '44px', padding: '12px 16px' }}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default UserForm; 